import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { cleanParams } from '@/api/client'
import { toApiError } from '@/api/errors'
import i18n from '@/i18n'
import type { ApiItem, ApiPage } from '@/types/api'

const baseURL = `${(import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1'}/platform`

/**
 * The operator console talks to /api/v1/platform with its own access token (memory only) and its
 * own refresh cookie, so a school session and an operator session never mix.
 */
let accessToken: string | null = null
let refreshHandler: (() => Promise<string | null>) | null = null
let refreshInFlight: Promise<string | null> | null = null

export function setPlatformToken(token: string | null): void {
  accessToken = token
}

export function setPlatformRefreshHandler(handler: (() => Promise<string | null>) | null): void {
  refreshHandler = handler
}

export const platformHttp = axios.create({
  baseURL,
  withCredentials: true,
  headers: { Accept: 'application/json' },
  timeout: 30_000,
})

platformHttp.interceptors.request.use((config) => {
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`)
  config.headers.set('Accept-Language', i18n.language)
  return config
})

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

platformHttp.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const apiError = toApiError(error)
    const config = axios.isAxiosError(error) ? (error.config as RetriableConfig | undefined) : undefined
    const isAuthCall = config?.url?.startsWith('/auth/') ?? false
    if (apiError.status === 401 && config && !config._retried && !isAuthCall && refreshHandler) {
      config._retried = true
      refreshInFlight ??= refreshHandler().finally(() => {
        refreshInFlight = null
      })
      const token = await refreshInFlight
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`)
        return platformHttp.request(config)
      }
    }
    throw apiError
  },
)

export const platformApi = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return (await platformHttp.get<ApiItem<T>>(url, config)).data.data
  },
  async page<T>(url: string, params?: object): Promise<ApiPage<T>> {
    return (await platformHttp.get<ApiPage<T>>(url, { params: cleanParams(params) })).data
  },
  async post<T>(url: string, body?: unknown): Promise<T> {
    return (await platformHttp.post<ApiItem<T>>(url, body)).data.data
  },
  async put<T>(url: string, body?: unknown): Promise<T> {
    return (await platformHttp.put<ApiItem<T>>(url, body)).data.data
  },
}
